import React, { useMemo } from "react";
import moment from "moment-timezone";

const PrayerTimingsPanel = ({ colorSettings = {}, prayerData, masjidTimeZone, nextEvent, isJummahMode = false }) => {
  const {
    bodyBackgroundColor = "#E6E9EE",
    bodyTextColor = "#000",
    highlightColor = "#D39B51",
  } = colorSettings;

  // 1. Structure Data
  const prayers = useMemo(() => {
    if (!prayerData) return [];

    const iqama = prayerData.iqamatimes || {};
    const adhan = (prayerData.startTimes && prayerData.startTimes.timings) 
                  ? prayerData.startTimes.timings 
                  : {};

    return [
      { name: "FAJR", adhan: adhan.fajr, iqamah: iqama.fajr },
      { name: "DHUHR", adhan: adhan.dhuhr, iqamah: iqama.dhuhr },
      { name: "ASR", adhan: adhan.asr, iqamah: iqama.asr },
      { name: "MAGHRIB", adhan: adhan.maghrib, iqamah: iqama.maghrib },
      { name: "ISHA", adhan: adhan.isha, iqamah: iqama.isha },
    ];
  }, [prayerData]);

  // 2. Calculate Active Prayer
  const activePrayer = useMemo(() => {
    if (isJummahMode) return null;
    if (nextEvent !== undefined) {
      if (!nextEvent) return null;
      const name = (nextEvent.name || "").toUpperCase();
      if (name === "NO_HIGHLIGHT") return null;
      const prayerNames = ["FAJR", "DHUHR", "ASR", "MAGHRIB", "ISHA"];
      if (prayerNames.includes(name)) return name;
      return null;
    }
    if (!prayers.length || !masjidTimeZone) return null;

    const now = moment.tz(masjidTimeZone);
    let current = null;

    for (let i = 0; i < prayers.length; i++) {
      const p = prayers[i];
      const nextP = prayers[i + 1] || prayers[0];
      if (!p.iqamah || !nextP.iqamah) continue;

      const iqamahMoment = moment.tz(p.iqamah, ["h:mm A", "hh:mm A"], masjidTimeZone);
      const nextIqamahMoment = moment.tz(nextP.iqamah, ["h:mm A", "hh:mm A"], masjidTimeZone);

      if (now.isBetween(iqamahMoment, nextIqamahMoment, null, "[)")) {
        current = nextP.name; 
        break;
      }
      if (i === prayers.length - 1 && now.isAfter(iqamahMoment)) {
        current = "FAJR";
        break;
      }
    }
    return current;
  }, [nextEvent, prayers, masjidTimeZone, isJummahMode]);

  const formatTime = (timeStr) => {
    if (!timeStr) return { mainTime: "--:--", meridiem: "" };
    const [time, meridiem] = timeStr.split(" ");
    const mainTime = time.replace(/^0/, "");
    return { mainTime, meridiem };
  };

  if (prayers.length === 0) {
    return <div className="text-center p-4">Loading...</div>;
  }

  // --- STYLE CONSTANTS ---
  // Ensuring every row (header, start, iqamah) has identical height structure
  const rowBaseClass = "flex-1 flex items-center";

  return (
    <div 
      className="w-full h-full p-2 flex items-stretch"
      style={{ backgroundColor: bodyBackgroundColor, color: bodyTextColor }}
    >
      
      {/* 1. LABELS COLUMN (Left Side) */}
      <div className="flex flex-col pr-3 py-2 min-w-[max-content]">
        
        {/* Spacer Row: Matches Prayer Name Height Exactly */}
        {/* We use &nbsp; and the exact same text size text-[3svh] to force equal height */}
        <div className={`${rowBaseClass} justify-end text-[3svh]`}>
          &nbsp;
        </div> 
        
        {/* Starts Label Row */}
        <div className={`${rowBaseClass} justify-end text-gray-600 font-semibold text-[3svh]`}>
          STARTS
        </div>
        
        {/* Iqamah Label Row */}
        <div className={`${rowBaseClass} justify-end text-gray-600 font-semibold text-[2.8svh]`}>
          IQAMAH
        </div>
      </div>

      {/* 2. PRAYER COLUMNS LOOP */}
      {prayers.map((p) => {
        const isActive = p.name === activePrayer;
        const adhan = formatTime(p.adhan);
        const iqamah = formatTime(p.iqamah);

        return (
          <div 
            key={p.name}
            className="flex-1 flex flex-col items-center transition-all duration-300 py-2 rounded-lg"
            style={{
              backgroundColor: isActive ? highlightColor : "transparent",
              transform: isActive ? "scale(1.02)" : "scale(1)",
              boxShadow: isActive ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : "none"
            }}
          >
            {/* Header: Prayer Name */}
            <div 
                className={`${rowBaseClass} justify-center font-bold text-[4svh]`}
                style={{ color: isActive ? "#000" : bodyTextColor }}
            >
              {p.name}
            </div>

            {/* Starts Time */}
            <div 
                className={`${rowBaseClass} justify-center items-baseline font-sans`}
                style={{ color: isActive ? "#000" : bodyTextColor }}
            >
              <span className="text-[3svh] leading-none">{adhan.mainTime}</span>
              {adhan.meridiem && <span className="text-[1.5svh] pl-[0.2svh]">{adhan.meridiem}</span>}
            </div>

            {/* Iqamah Time */}
            <div 
                className={`${rowBaseClass} justify-center items-baseline`}
                style={{ color: isActive ? "#000" : bodyTextColor }}
            >
              <span className="text-[6svh] leading-none">{iqamah.mainTime}</span>
              {iqamah.meridiem && <span className="text-[2.5svh] pl-[0.2svh]">{iqamah.meridiem}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrayerTimingsPanel;